import React from 'react';
import Icon, { ICONS, type IconName } from '../../../ui/icons';
import { useLocalization, type Language } from '../../../../config/localization';

export type SectionId = 'general' | 'widgets' | 'dynamic_widget' | 'api_endpoints' | 'tile_config' | 'tags' | 'prompt' | 'content_restrictions' | 'input_text' | 'slots_input_media' | 'pipeline_steps' | 'enhance_mappings' | 'api';

export interface SectionStatus {
  hasErrors: boolean;
  isComplete: boolean;
}

interface WorkflowModalSidebarProps {
  activeSection: SectionId;
  onSectionChange: (section: SectionId) => void;
  sectionStatus: Record<SectionId, SectionStatus>;
  language: Language;
  workflowTitle?: string;
  hiddenSections?: Set<SectionId>;
}

interface SidebarItem {
  id: SectionId;
  icon: IconName;
  labelKey: string;
  fallback: string;
}

export const sidebarItems: SidebarItem[] = [
  { id: 'general', icon: ICONS.SETTINGS, labelKey: 'general', fallback: 'General' },
  { id: 'widgets', icon: ICONS.GRID, labelKey: 'widgets', fallback: 'Widgets' },
  { id: 'dynamic_widget', icon: ICONS.SLIDERS, labelKey: 'dynamicWidget', fallback: 'Dynamic Widget' },
  { id: 'api_endpoints', icon: ICONS.LINK, labelKey: 'apiEndpoints', fallback: 'Dify Endpoints' },
  { id: 'tile_config', icon: ICONS.IMAGE, labelKey: 'tileConfig', fallback: 'Tile Config' },
  { id: 'tags', icon: ICONS.TAG, labelKey: 'tags', fallback: 'Tags' },
  { id: 'prompt', icon: ICONS.PROMPTS, labelKey: 'references', fallback: 'References' },
  { id: 'content_restrictions', icon: ICONS.SHIELD, labelKey: 'restrictions', fallback: 'Restrictions' },
  { id: 'input_text', icon: ICONS.EDIT, labelKey: 'inputTextSettings', fallback: 'Input Text' },
  { id: 'slots_input_media', icon: ICONS.UPLOAD, labelKey: 'slotsInputMediaSettings', fallback: 'Slots Media' },
  { id: 'pipeline_steps', icon: ICONS.LAYERS, labelKey: 'pipelineSteps', fallback: 'Pipeline Steps' },
  { id: 'enhance_mappings', icon: ICONS.SPARKLES, labelKey: 'enhanceMappings', fallback: 'Enhance Mappings' },
  { id: 'api', icon: ICONS.CODE, labelKey: 'api', fallback: 'API' },
];

export default function WorkflowModalSidebar({
  activeSection,
  onSectionChange,
  sectionStatus,
  language,
  workflowTitle,
  hiddenSections
}: WorkflowModalSidebarProps) {
  const t = useLocalization(language);

  const visibleItems = hiddenSections
    ? sidebarItems.filter(item => !hiddenSections.has(item.id))
    : sidebarItems;

  return (
    <aside className="w-56 bg-gray-800 border-r border-gray-700 flex flex-col flex-shrink-0">
      {/* Header */}
      <div className="p-4 border-b border-gray-700">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <Icon name={ICONS.EDIT} size={20} className="text-blue-400" />
          <span className="truncate">{t.admin?.editWorkflow || 'Edit Workflow'}</span>
        </h3>
        {workflowTitle && (
          <p className="text-sm text-gray-400 mt-1 truncate" title={workflowTitle}>
            {workflowTitle}
          </p>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-2 space-y-1">
        {visibleItems.map(item => {
          const status = sectionStatus[item.id];
          const isActive = activeSection === item.id;

          return (
            <button
              key={item.id}
              onClick={() => onSectionChange(item.id)}
              className={`
                w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all duration-150
                ${isActive
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20'
                  : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }
              `}
            >
              <Icon
                name={item.icon}
                size={18}
                className={isActive ? 'text-white' : 'text-gray-400'}
              />
              <span className="flex-1 text-sm font-medium">
                {(t.admin as any)?.[item.labelKey] || item.fallback}
              </span>

              {/* Status indicator */}
              {status.hasErrors && (
                <span
                  className="w-2 h-2 rounded-full bg-red-500 animate-pulse"
                  title={t.admin?.hasErrors || 'Has errors'}
                />
              )}
              {status.isComplete && !status.hasErrors && (
                <span
                  className="w-2 h-2 rounded-full bg-green-500"
                  title={t.admin?.complete || 'Complete'}
                />
              )}
            </button>
          );
        })}
      </nav>

      {/* Footer hint */}
      <div className="p-3 border-t border-gray-700">
        <p className="text-xs text-gray-500 text-center">
          {t.admin?.clickToNavigate || 'Click to navigate between sections'}
        </p>
      </div>
    </aside>
  );
}
